import { z } from "zod";

export const TrackingHistorySchema = z.object({
    id: z.number(),
    contact: z.string().nullable().optional(),
    currentLocation: z.string().nullable().optional(),
    deliveryPerson: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    level: z.number().nullable().optional(),
    time: z.string().nullable().optional(),
    orderId: z.number().nullable().optional(),
}).passthrough();

export type TrackingHistoryEntity = z.infer<typeof TrackingHistorySchema>;

export const OrderOptionResponseSchema = z.object({
    optionName: z.string(),
    optionValue: z.string(),
    optionCount: z.number(),
    optionPrice: z.number(),
    optionTotalPrice: z.number(),
    quantityApplyType: z.string().optional(),
}).passthrough();

export type OrderOptionResponse = z.infer<typeof OrderOptionResponseSchema>;

export const OrderWithOptionsResponseSchema = z.object({
    id: z.number(),
    name: z.string().nullable().optional(),
    orderStatus: z.string(),
    orderTotalAmount: z.number().nullable().optional(),
    productCount: z.number().nullable().optional(),
    productPrice: z.number().nullable().optional(),
    productTotalAmount: z.number().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    submittedAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
    orderNumber: z.string().nullable().optional(),
    purchaserName: z.string().nullable().optional(),
    purchaserEmail: z.string().nullable().optional(),
    orderSourceFile: z.string().nullable().optional(),
    options: z.union([z.array(z.string()), z.array(OrderOptionResponseSchema)]).nullable().optional(),
    trackingNumber: z.string().nullable().optional(),
    courier: z.string().nullable().optional(),
    trackingHistory: z.array(TrackingHistorySchema).nullable().optional(),
    product: z.any().nullable().optional(),
    memo: z.string().nullable().optional()
}).passthrough();

export type OrderEntity = z.infer<typeof OrderWithOptionsResponseSchema>;

export const GetOrderResponseSchema = z.array(OrderWithOptionsResponseSchema);

export type GetOrdersResponse = z.infer<typeof GetOrderResponseSchema>;

// 주문 생성 관련 타입
export type GuestInfo = {
  name: string;
  email?: string;
  mobile?: string;
};

export type OrderOption = {
  combinationId: number;
  combinationKeyId: string;
  optionCount: number;
  quantityApplyType: "PER_ITEM" | "PER_ORDER" | "SAME_AS_PRODUCT_QUANTITY" | "SELECTABLE";
  snapshot: string;
  optionPrice: number;
};

export type CalculateOption = {
  basePrice: number;
  optionPrice: number;
  totalCount: number;
};

// 커스텀 주문 옵션
export type CustomOrderOption = {
  optionName: string;
  optionPrice: number;
  optionCount: number;
};

// 커스텀 주문 정보
export type CustomOrderInfo = {
  customProductName: string;
  productPrice: number;
  productCount: number;
  customOptions?: CustomOrderOption[];
};

export type CreateOrderRequest = {
  name: string;
  productId?: number;
  productCount?: number;
  productPrice?: number;
  customOrderInfo?: CustomOrderInfo;
  initialStatus?: "BEFORE_PAYMENT" | "RECEIVED";
  purchaserId?: number;
  guestInfo?: GuestInfo;
  options?: OrderOption[];
  calculateOption?: CalculateOption;
  memo?: string;
  courier?: string;
  trackingNumber?: string;
  invoiceType?: "TAX" | "CASH_RECEIPT" | "NONE";
};

export type CreateOrderResponse = {
  orderId: number;
  verificationCode?: string;
  isGuestOrder: boolean;
};

// 택배사 타입
export type Courier = {
  code: string;
  name: string;
  title: string;
};

// 일괄 출고등록 응답 타입
export type BulkShipmentFailure = {
  rowNumber: number;
  orderNumber: string | null;
  reason: string;
};

export type BulkShipmentRegisterResponse = {
  totalCount: number;
  successCount: number;
  failCount: number;
  failures: BulkShipmentFailure[];
};

// 페이징 응답 타입
export type OrderPageResponse = {
  items: OrderEntity[];
  totalPages: number;
  totalElements: number;
  pageSize: number;
  next?: string | null;
  previous?: string | null;
};

/** 주문관리 검색창 추천어. CUSTOMER=업체, CATEGORY=종목 */
export type OrderSearchSuggestion = { type: "CUSTOMER" | "CATEGORY"; value: string };
